import { useCan } from '@features/auth';
import {
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    IconPlus,
    LoadingRows,
} from '@shared/ui';
import { useMemo, useState } from 'react';

import { useComponents } from '../hooks/use-components';

import { sortComponents } from './component-list';
import { CreateComponentDialog } from './CreateComponentDialog';

import type { Component } from '../schemas/component';

/**
 * The middle layer: a component groups flags into one thing Product can name
 * and Ops can activate against a museum.
 *
 * ## What used to be here, and why it is not any more
 *
 * This tab rendered a `ComingSoonPanel` whose reasoning was sound at the time:
 * a flag that exists to be sold is born knowing it — that is what
 * `purpose: entitlement` means on a flag definition — but nothing grouped those
 * flags yet, and inventing the grouping in the UI before the catalog defined it
 * would have meant guessing what a component contains. The catalog has since
 * defined it: `GET`/`POST /api/components`, with membership restricted to
 * entitlement flags server-side. The guess is gone, so the placeholder goes too.
 *
 * What is still missing is deliberate rather than unfinished. A component holds
 * no activation state, so there is nothing here to switch on: whether a museum
 * has this component is a fact about that museum's override rows, and it lands
 * with activation. Editing a component is likewise absent — the API has no
 * write for it yet.
 */

/** Hoisted so `useMemo` below has a stable empty array to depend on. */
const NO_ROWS: readonly Component[] = [];

export function CatalogComponentsTab() {
    const components = useComponents();
    const [creating, setCreating] = useState(false);
    // Must match `@RequiresRole('engineer')` on `POST /api/components`. The
    // operator decision of 2026-09-21 recorded on the controller: an id is
    // permanent and its members decide what a museum can be billed for.
    const canCreate = useCan('engineer');

    // A `?? []` inline would mint a new array on every render and defeat the memo.
    const rows = components.data ?? NO_ROWS;
    const sorted = useMemo(() => sortComponents(rows), [rows]);

    const createButton = (label: string) => (
        <Button
            disabled={!canCreate}
            onClick={() => {
                setCreating(true);
            }}
        >
            <IconPlus className="size-4" />
            {label}
        </Button>
    );

    return (
        <>
            {/* No top margin: `TabStrip` above already carries `mb-5`, the
                spacing every other catalog tab sits under. */}
            <Card>
                <CardHeader
                    title="Components"
                    subtitle="Bundles of entitlement flags. Defining one grants nothing."
                    action={createButton('New component')}
                />

                {components.isPending ? (
                    <LoadingRows label="Loading components…" />
                ) : components.isError ? (
                    <ErrorState
                        error={components.error}
                        onRetry={() => void components.refetch()}
                    />
                ) : sorted.length === 0 ? (
                    <EmptyState
                        title="No components yet"
                        description="A component bundles the entitlement flags that make up one sellable capability. Creating one turns nothing on for anybody — it is activated against a museum separately."
                        action={createButton('Create the first component')}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-surface-sunken text-left text-[11px] uppercase tracking-wide text-content-muted">
                                    <th className="px-5 py-3 font-semibold">Component</th>
                                    <th className="px-5 py-3 font-semibold">Id</th>
                                    <th className="px-5 py-3 font-semibold">Owner</th>
                                    <th className="px-5 py-3 font-semibold">Members</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((component) => (
                                    <ComponentRow key={component.id} component={component} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <CreateComponentDialog
                open={creating}
                onClose={() => {
                    setCreating(false);
                }}
                // Nowhere to navigate to: a component has no detail page, and
                // the row it just created is in the table behind the dialog.
                onCreated={() => {
                    setCreating(false);
                }}
            />
        </>
    );
}

/**
 * One row. The members are listed in full rather than counted: the count is the
 * one thing an operator can already see, and *which* flags a museum is being
 * sold is the question this table exists to answer.
 */
function ComponentRow({ component }: { component: Component }) {
    return (
        <tr className="border-t border-line-subtle align-top text-[13.5px]">
            <td className="px-5 py-3">
                <div className="font-semibold text-content-strong">{component.displayName}</div>
                <div className="mt-0.5 max-w-md text-xs leading-relaxed text-content-muted">
                    {component.description}
                </div>
            </td>
            <td className="px-5 py-3">
                <FlagKeyPill>{component.id}</FlagKeyPill>
            </td>
            <td className="px-5 py-3 text-content-body">{component.owner}</td>
            <td className="px-5 py-3">
                {component.flagKeys.length === 0 ? (
                    // Not an error state. A bundle may be assembled before the
                    // flags it will hold exist, which is why the API accepts it.
                    <span className="text-xs text-content-faint">No flags yet</span>
                ) : (
                    <div className="flex max-w-lg flex-wrap gap-1.5">
                        {component.flagKeys.map((key) => (
                            <FlagKeyPill key={key}>{key}</FlagKeyPill>
                        ))}
                    </div>
                )}
            </td>
        </tr>
    );
}
